import React from 'react';
import {
  BasicInfoCard,
  AddedComponentsCard,
  NewComponentConfigCard,
  AvailableComponentsCard,
  ReportSectionCard,
} from '@/pages/reports/new/components';
import { ReportAccessDenied } from '@/pages/reports/components/ReportAccessDenied';
import { useReportEdit } from './hooks/useReportEdit';
import {
  ReportEditLayout,
  ReportEditBackLink,
  ReportEditHeader,
  ReportEditLoading,
  ReportEditActions,
  ReportDeleteDialog,
} from './components';

export const ReportEditPage: React.FC = () => {
  const {
    id,
    loading,
    accessDenied,
    title,
    titleError,
    edition,
    editionError,
    onEditionChange,
    components,
    selectedComponent,
    componentProps,
    validationErrors,
    editingComponentId,
    isSaving,
    isDeleting,
    deleteDialogOpen,
    setDeleteDialogOpen,
    partnerId,
    partnerError,
    lang,
    langError,
    componentsError,
    setPartnerId,
    onLangChange,
    partnerOptions,
    thematicTemplates,
    selectedTemplateId,
    onTemplateSelect,
    onClearAll,
    handleTitleChange,
    handleAddComponent,
    handleEditComponent,
    handleSaveComponent,
    handleRemoveComponent,
    handleMoveComponent,
    handlePropChange,
    handleFinalizeClose,
    handleFinalizeSend,
    handleDeleteReport,
    cancelComponentEdit,
    saveCurrentAsTemplate,
    updateSelectedTemplate,
    deleteSelectedTemplate,
    navigate,
    autoSaveStatus,
    lastSavedAt,
    openSection,
    handleSectionOpenChange,
  } = useReportEdit();

  if (accessDenied) {
    return <ReportAccessDenied />;
  }

  if (loading) {
    return (
      <ReportEditLayout>
        <ReportEditBackLink onBack={() => navigate(-1)} />
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Editar Report</h1>
          <p className="text-lg text-gray-600">
            Edite seu report modificando e configurando componentes
          </p>
        </div>
        <ReportEditLoading />
      </ReportEditLayout>
    );
  }

  return (
    <ReportEditLayout>
      <ReportEditBackLink onBack={() => navigate(-1)} />
      <ReportEditHeader
        reportId={id}
        autoSaveStatus={autoSaveStatus}
        lastSavedAt={lastSavedAt}
      />

      <div className="space-y-6">
        <div id="report-basic-section">
          <ReportSectionCard
            title="Dados do report"
            description="Informações básicas para identificar o report"
            open={openSection === 'basic'}
            onOpenChange={(open) => handleSectionOpenChange('basic', open)}
            completed={true}
          >
            <BasicInfoCard
              title={title}
              titleError={titleError}
              onTitleChange={handleTitleChange}
              edition={edition}
              editionError={editionError}
              onEditionChange={onEditionChange}
              partnerId={partnerId}
              partnerError={partnerError}
              onPartnerChange={setPartnerId}
              partnerOptions={partnerOptions}
              lang={lang}
              langError={langError}
              onLangChange={onLangChange}
            />
          </ReportSectionCard>
        </div>

        <div id="report-components-section">
          <ReportSectionCard
            title="Componentes adicionados"
            description="Gerencie e reordene os componentes do seu report"
            open={openSection === 'components'}
            onOpenChange={(open) => handleSectionOpenChange('components', open)}
          >
            <div className="space-y-8">
              <AddedComponentsCard
                components={components}
                editingComponentId={editingComponentId}
                selectedComponent={selectedComponent}
                componentProps={componentProps}
                validationErrors={validationErrors}
                onEditComponent={handleEditComponent}
                onRemoveComponent={handleRemoveComponent}
                onMoveComponent={handleMoveComponent}
                onPropChange={handlePropChange}
                onSaveComponent={handleSaveComponent}
                onCancelEdit={cancelComponentEdit}
                thematicTemplates={thematicTemplates}
                selectedTemplateId={selectedTemplateId}
                onTemplateSelect={onTemplateSelect}
                onClearAll={onClearAll}
                onSaveTemplate={saveCurrentAsTemplate}
                onUpdateTemplate={updateSelectedTemplate}
                onDeleteTemplate={deleteSelectedTemplate}
              />

              {selectedComponent && !editingComponentId && (
                <NewComponentConfigCard
                  selectedComponent={selectedComponent}
                  componentProps={componentProps}
                  validationErrors={validationErrors}
                  onPropChange={handlePropChange}
                  onSave={handleSaveComponent}
                  onCancel={cancelComponentEdit}
                  thematicTemplates={thematicTemplates}
                  selectedTemplateId={selectedTemplateId}
                  onTemplateSelect={onTemplateSelect}
                  onSaveTemplate={saveCurrentAsTemplate}
                  onUpdateTemplate={updateSelectedTemplate}
                  onDeleteTemplate={deleteSelectedTemplate}
                />
              )}

              <AvailableComponentsCard
                onAddComponent={handleAddComponent}
                hideHeader
                disabledTypes={
                  components.some((component) => component.type === 'hero')
                    ? ['hero']
                    : []
                }
              />
            </div>
          </ReportSectionCard>
        </div>

        <ReportEditActions
          isSaving={isSaving}
          onFinalizeClose={handleFinalizeClose}
          onFinalizeSend={handleFinalizeSend}
          onDelete={() => setDeleteDialogOpen(true)}
          errorMessage={componentsError}
        />

        <ReportDeleteDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title={title}
          isDeleting={isDeleting}
          onConfirm={handleDeleteReport}
        />
      </div>
    </ReportEditLayout>
  );
};
